import { Blockable } from '@agilie/angular-helpies/decorators';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { atlPackagesSliderSwiperOptions } from '@atlas-workspace/shared/directives';
import { ModalHelpersService } from '@atlas-workspace/shared/modals';
import {
  CategoryProductModel,
  ClientIframePackageDataModel,
  ClientIframeProductModel,
  ClientLayoutTypeCategoryData,

  ClientPackageModel,
  DocumentModel,
  EMixpanelEvent,
  EPriceType,
  EWishlistStatus,
  ImageModel,
  ISettingsMenu,
  ProductListItemModelCPB,
  ProductModel,
} from '@atlas-workspace/shared/models';
import {
  MixpanelService,
} from '@atlas-workspace/shared/service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash';
import { of, Subscription } from 'rxjs';
import { delay, filter, take, tap } from 'rxjs/operators';
import { OptionsIframeService } from '../services/options-iframe.service';
import { HelpOptiosModalService } from '../../options-catalogue/services/help-options-modal.service';


@UntilDestroy()
@Component({
  selector: 'atl-options-iframe-packages',
  templateUrl: './packages.component.html',
  styleUrls: ['./packages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HelpOptiosModalService]
})
export class IframePackagesComponent implements OnInit {
  packageData!: ClientIframePackageDataModel;

  public totalPrice: number | null = null;
  public nextCategoryRoute!: string;
  public introductionRoute!: string;
  public readonly wishlistStatus = EWishlistStatus;

  public isGuest!: boolean;
  readonly tooltipOpenDelay = 400;

  public loading = true;
  public categoryData!: ClientLayoutTypeCategoryData;
  public activePackage?: ClientPackageModel;
  public productDetails: ProductModel | null = null;
  readonly packageNameLimit = 40;

  readonly imageSliderOptions = cloneDeep(atlPackagesSliderSwiperOptions);
  public resettingProduct = false;
  readonly maxProductLen = 40;
  readonly mobileMaxProductLen = 20;
  private readonly mobileViewSize = 768;
  public expandedDescription = false;
  readonly priceType = EPriceType;
  private selectionToken!: string;
  private tokenId!: string;
  showPrices = false;

  constructor(
    private readonly optionsIframeService: OptionsIframeService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly mixpanelService: MixpanelService,
    private readonly modalHelpersService: ModalHelpersService
  ) { }

  ngOnInit(): void {
    this.getPackages();
    this.tokenId = this.route?.parent?.snapshot.params['tokenId'];
    this.getNextCategory();
    this.mixpanelService.track(EMixpanelEvent.PagePackages);
    this.getSelectionToken();
    this.getShowPrices();
  }

  private getNextCategory(): void {
    this.optionsIframeService.optionCategories$.pipe(untilDestroyed(this)).subscribe((res: ISettingsMenu[]) => {
      const cat = res.find(x => !x.route.includes('introduction') && !x.route.includes('packages'));
      if (cat) {
        this.nextCategoryRoute = cat.route;
      }
    });
  }

  private getSelectionToken(): void {
    this.optionsIframeService.selectionToken$.pipe(
      filter(x => !!x),
      untilDestroyed(this)
    )
      .subscribe(token => {
        this.selectionToken = token;
      });
  }

  onPreviewDocument(document: DocumentModel): void {
    this.modalHelpersService.previewDocument(document, true);
  }

  updateToggleState(value: boolean): void {
    this.expandedDescription = value;
    this.cdr.markForCheck();
  }

  onPreviewImage(index: number, images: ImageModel[]): void {
    this.modalHelpersService.previewImages(images ?? [], index, true, false);
  }

  closeProductDetails(): void {
    this.productDetails = null;
    this.cdr.markForCheck();
  }

  get isMobileView(): boolean {
    return this.mobileViewSize >= window.innerWidth;
  }

  afterInitSwiperHandler(): void {
    queueMicrotask(() => this.cdr.detectChanges());
  }


  selectCategory(categoryId: number, categories: CategoryProductModel[]): void {
    categories.forEach((el) => {
      el.selected = categoryId === el.id;
    });
  }

  selectPackage(packageData: ClientPackageModel): void {
    if (this.activePackage?.id === packageData.id) {
      return;
    }
    this.expandedDescription = false;
    this.resettingProduct = true;
    this.productDetails = null;
    this.activePackage = { ...packageData };

    of(true).pipe(delay(50)).subscribe(() => {
      this.resettingProduct = false;
      this.cdr.markForCheck();
    });
  }

  openProductDetails(product: ProductListItemModelCPB): void {
    this.expandedDescription = false;
    this.optionsIframeService
      .getIframeProduct(this.tokenId, +product.id, this.selectionToken)
      .pipe(
        take(1)
      )
      .subscribe((result: ProductModel) => {
        this.productDetails = result;
        this.cdr.markForCheck();
      });
  }


  private unselectDesignLine(selectedPackage: ClientPackageModel): void {
    this.optionsIframeService.unselectPackage(this.tokenId, selectedPackage.id, this.selectionToken).pipe(
      take(1),
      tap(() => {
        this.updateSelectedItems();
      })
    )
      .subscribe(() => {
        selectedPackage.selected = true;
        this.packageData.packages.forEach(p => {
          p.selected = false
        });
        this.updateExistingPackages();
        void this.router.navigate([this.nextCategoryRoute]);
      });
  }

  private updateSelectedItems(): void {
    this.optionsIframeService.getIframeSelectedItems(this.tokenId, this.selectionToken).pipe(take(1))
    .subscribe(((res: ClientIframeProductModel[]) => {
        const totalPrice = res?.reduce((total, x) => total + Number(x.fullPrice), 0);
        this.optionsIframeService.setTotalPrice(totalPrice);
    }));
  }

  confirmSelectPackage(clientPackage: ClientPackageModel): void {
    this.optionsIframeService.selectPackage(this.tokenId, clientPackage.id).pipe(take(1))
      .subscribe((newToken: string) => {
       this.selectionToken = newToken;
        this.optionsIframeService.setSelectionToken(newToken);
        clientPackage.selected = true;
        this.packageData.packages.forEach(p => {
          p.selected = p.id === clientPackage.id;
        });
        this.updateExistingPackages();
        this.updateSelectedItems();
        this.optionsIframeService.setSelectedPackage(clientPackage);
       if (!this.packageData.optionDesignLineProducts) {
          void this.router.navigate([this.nextCategoryRoute]);
       } 
      });
  }

  @Blockable() packageSelectionHandler(clientPackage: ClientPackageModel): Subscription | void {
    if (this.packageData.optionDesignLineProducts) {
      const selectedPackage = this.packageData.packages.find(x => x.selected);
      if (!selectedPackage) {
        void this.router.navigate([this.nextCategoryRoute]);
        return;
      }
      this.unselectDesignLine(selectedPackage);
    } else {
      if (clientPackage.selected) {
        this.unselectDesignLine(clientPackage);
      } else {
        this.confirmSelectPackage(clientPackage);
      }

    }
  }

  private updateExistingPackages(): void {
    this.optionsIframeService.setPackages(this.packageData);
  }

  private getPackages(): void {
    this.optionsIframeService.packages$.pipe(untilDestroyed(this)).subscribe((data) => {
      if (data) {
        if (Array.isArray(data.packages)) {
          const { packages } = data;
          const filteredPackages = packages.filter(pkg => Array.isArray(pkg.categories) && pkg.categories.length > 0);
          this.packageData = {
            ...data,
            packages: filteredPackages
          };
          const selectedPackage = filteredPackages.find(x => x.selected);
          if (selectedPackage) {
            this.activePackage = selectedPackage;
          } else {
            [this.activePackage] = filteredPackages;
          }
        }
        this.setActiveCategory();
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private setActiveCategory(): void {
    this.packageData.packages.forEach((p: ClientPackageModel) => {
      p.categories.forEach((category, i) => {
        category.selected = i === 0;
      });
    });
    this.cdr.markForCheck();
  }

  public navigateToSummary(): void {
    this.router.navigate(['../summary'], { relativeTo: this.route });

  }

  private getShowPrices(): void {
    this.optionsIframeService.showPrices$.pipe(untilDestroyed(this))
      .subscribe((showPrices: boolean) => {
        this.showPrices = showPrices;
      });
  }

}
